import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { toast } from '@/components/ui/use-toast';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import { useAuthContext } from '@/contexts/AuthContext';
import { formatCurrency, formatDocument } from '@/lib/utils';
import {
  getBusinessOpportunity,
  getHubspotOpportunityById,
  getHubspotLineItemsByDealId,
  mapHubspotStageToStatus,
  getHubspotQuotesByDealId
} from '@/services/business-opportunities';
import {
  BusinessOpportunity,
  HubspotOpportunity,
  HubspotLineItem,
  OpportunityStatus
} from '@/types/business-opportunities';
import { 
  ArrowLeft, 
  Building,
  Calendar,
  ChevronLeft,
  Copy,
  Edit, 
  FileText, 
  MoreHorizontal, 
  Package, 
  Pencil,
  Plus, 
  RefreshCw, 
  Trash2,
  User,
  DollarSign,
  ExternalLink,
  Link,
  Users,
  Percent,
  CalendarRange,
  BarChart4
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { supabase } from '@/lib/supabase';
import { initializeHubspotTables, checkHubspotOpportunityExists } from '@/utils/initialize-hubspot-data';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { format } from 'date-fns';
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { getBusinessOpportunityById } from "@/lib/services/business-opportunities";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

type OpportunityWithDetails = BusinessOpportunity & {
  customer: {
    id: string;
    name: string;
    logo_url?: string;
    business_identification_number?: string;
    legal_name?: string;
    address?: string;
    city?: string;
    state?: string;
    zip?: string;
  };
};

// Extended type for Hubspot opportunities with line items
type HubspotOpportunityWithDetails = HubspotOpportunity & {
  customer?: {
    id: string;
    name: string;
    logo_url?: string;
    business_identification_number?: string;
    legal_name?: string;
    address?: string;
    city?: string;
    state?: string;
    zip?: string;
    // Additional fields from hubspotcompanies view
    industry?: string;
    phone?: string;
    ownername?: string;
    owneremail?: string;
    annualrevenue?: number;
    domain?: string;
    alunos?: number;
    classificacao?: string;
    razao_social?: string;
    faixa_alunos?: string;
    hubspotscore?: number;
    lifecyclestage?: string;
    customer_address?: string; // Added customer_address field
  };
  line_items?: HubspotLineItem[];
  quotes?: Array<{
    id: string;
    hs_quote_number?: string;
    hs_status?: string;
    hs_quote_amount?: number;
    hs_currency?: string;
    hs_createdate?: string;
    hs_expiration_date?: string;
    hs_quote_link?: string;
    hs_pdf_download_link?: string;
    deal_id?: string;
    hs_terms?: string;
    hs_sender_firstname?: string;
    hs_sender_lastname?: string;
    hs_sender_email?: string;
  }>;
  owner_name?: string; // Owner name at the opportunity level
  label?: string; // Added label field for deal stage
};

const statusLabels = {
  open: 'Aberta',
  negotiation: 'Em Negociação',
  won: 'Ganha',
  lost: 'Perdida',
  canceled: 'Cancelada'
};

const statusColors = {
  open: 'bg-blue-50 text-blue-700 border-blue-200',
  negotiation: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  won: 'bg-green-50 text-green-700 border-green-200',
  lost: 'bg-red-50 text-red-700 border-red-200',
  canceled: 'bg-gray-50 text-gray-700 border-gray-200'
};

const BusinessOpportunityDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuthContext();
  
  const [opportunity, setOpportunity] = useState<OpportunityWithDetails | null>(null);
  const [hubspotOpportunity, setHubspotOpportunity] = useState<HubspotOpportunityWithDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [errorDetails, setErrorDetails] = useState<{message: string, technical?: string}>({ message: '' });
  const [isHubspotOpportunity, setIsHubspotOpportunity] = useState(false);
  const [showHubspotData, setShowHubspotData] = useState(true);
  // Add state for tracking failed image loads
  const [failedImages, setFailedImages] = useState<Record<string, boolean>>({});

  // Helper function for Hubspot stage labels
  const getHubspotStageLabel = (dealstage: string): string => {
    // Convert dealstage IDs to readable labels
    if (dealstage.includes('closedwon')) return 'Ganha';
    if (dealstage.includes('closedlost')) return 'Perdida';
    if (dealstage.includes('qualifiedtobuy')) return 'Qualificada';
    if (dealstage.includes('presentation')) return 'Apresentação';
    if (dealstage.includes('decisionmaker')) return 'Tomador de Decisão';
    if (dealstage.includes('proposal')) return 'Proposta';
    if (dealstage.includes('negotiation')) return 'Negociação';
    if (dealstage.includes('appointment')) return 'Reunião Agendada';
    if (dealstage.includes('discovery')) return 'Descoberta';
    if (dealstage.includes('demo')) return 'Demonstração';
    return dealstage; // Return the original value if no match
  };

  // Error display component
  const ErrorDisplay = () => (
    <div className="container mx-auto py-10">
      <div className="max-w-lg mx-auto bg-white rounded-lg shadow-sm p-6 text-center">
        <div className="flex items-center justify-center h-16 w-16 rounded-full bg-red-50 mx-auto mb-4">
          <div className="h-10 w-10 text-red-500">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
          </div>
        </div>
        <h2 className="text-2xl font-extrabold text-gray-900 mb-2">Erro ao carregar a oportunidade</h2>
        <p className="text-gray-600 mb-6">{errorDetails.message}</p>
        {errorDetails.technical && (
          <div className="bg-gray-50 p-3 rounded text-left mb-6">
            <p className="text-sm text-gray-500 mb-1">Detalhes técnicos:</p>
            <code className="text-xs text-gray-700 block overflow-x-auto">
              {errorDetails.technical}
            </code>
          </div>
        )}
        <Button 
          onClick={() => navigate('/business-opportunities')} 
          variant="outline"
          className="mr-3"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar para lista
        </Button>
        <Button onClick={() => window.location.reload()}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Tentar novamente
        </Button>
      </div>
    </div>
  );

  useEffect(() => {
    const fetchOpportunityData = async () => {
      if (!id) {
        setErrorDetails({
          message: 'ID da oportunidade não fornecido',
          technical: 'Nenhum ID foi fornecido na URL'
        });
        setIsError(true);
        setIsLoading(false);
        return;
      }
      
      try {
        setIsLoading(true);
        
        // Initialize Hubspot tables if they don't exist
        await initializeHubspotTables();
        
        // Check if this is a Hubspot opportunity first
        const isHubspotOpp = await checkHubspotOpportunityExists(id);
        
        if (isHubspotOpp) {
          // Fetch the Hubspot opportunity with its related customer
          const hubspotOpp = await getHubspotOpportunityById(id);
          
          if (!hubspotOpp) {
            throw new Error(`Opportunity with ID ${id} not found in Hubspot`);
          }
          
          // Fetch line items related to this deal
          const lineItems = await getHubspotLineItemsByDealId(id);
          
          // Fetch quotes related to this deal
          const quotes = await getHubspotQuotesByDealId(id);
          
          // Create combined data structure with type casting to match expected types
          const opportunityData: HubspotOpportunityWithDetails = {
            ...hubspotOpp,
            customer_name: hubspotOpp.customer?.name || '',
            line_items: lineItems.map(item => ({
              id: item.id,
              deal_id: item.deal_id,
              name: item.name,
              quantity: item.quantity || 0, // Ensure required properties have defaults
              price: item.price || 0,
              amount: item.amount || 0,
              discount: item.discount || 0,
              description: item.description,
              createdate: item.createdate,
              hs_product_currency_code: item.hs_product_currency_code,
              hs_createdate: item.hs_createdate,
              hs_object_id: item.hs_object_id
            })),
            quotes: quotes
          };
          
          // Fix: Ensure customer exists
          if (!opportunityData.customer) {
            opportunityData.customer = {
              id: opportunityData.company_id || '',
              name: opportunityData.customer_name || ''
            };
          }
          
          // Fix: Derive customer_address if not provided but address components exist
          if (!opportunityData.customer.customer_address && opportunityData.customer.address) {
            const addressParts = [
              opportunityData.customer.address,
              opportunityData.customer.city,
              opportunityData.customer.state,
              opportunityData.customer.zip
            ].filter(Boolean);
            
            if (addressParts.length > 0) {
              opportunityData.customer.customer_address = addressParts.join(', ');
            }
          }
          
          setHubspotOpportunity(opportunityData);
          setIsHubspotOpportunity(true);
        } else {
          // Fetch regular opportunity
          const regularOpportunity = await getBusinessOpportunity(id);
          setOpportunity(regularOpportunity);
          setIsHubspotOpportunity(false);
        }
      } catch (error) {
        console.error('Error fetching opportunity data:', error);
        setErrorDetails({
          message: 'Erro ao carregar os dados da oportunidade',
          technical: error.message
        });
        setIsError(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchOpportunityData();
  }, [id]);

  const handleBackClick = () => {
    navigate('/business-opportunities');
  };

  const handleQuoteClick = (quote: any) => {
    // Sempre navegar para a página interna de detalhes da proposta
    navigate(`/proposals/${quote.id}`);
  };

  const formatAddress = (customer: any) => {
    if (!customer) return 'Endereço não disponível';
    
    const addressParts = [
      customer.address,
      customer.city,
      customer.state,
      customer.zip
    ].filter(Boolean);
    
    return addressParts.length > 0 ? addressParts.join(', ') : 'Endereço não disponível';
  };

  // Add a function to handle image loading errors
  const handleImageError = (id: string) => {
    setFailedImages(prev => ({
      ...prev,
      [id]: true
    }));
  };

  // Add a function to get initials for fallback avatar
  const getInitials = (name: string): string => {
    if (!name) return '?';
    return name
      .split(' ')
      .map(part => part.charAt(0))
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  // Show error if something went wrong
  if (isError) {
    return <ErrorDisplay />;
  }

  // Show loading state
  if (isLoading) {
    return (
      <div className="px-6 py-8 mx-auto max-w-[1920px] w-full">
        <div className="flex justify-center items-center flex-col h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          <p className="text-lg text-gray-500 mt-4">Carregando detalhes da oportunidade...</p>
        </div>
      </div>
    );
  }
  
  // If we have no data after loading is complete
  if (!opportunity && !hubspotOpportunity) {
    return (
      <div className="px-6 py-8 mx-auto max-w-[1920px] w-full">
        <div className="text-center">
          <div className="h-16 w-16 text-yellow-500 mx-auto mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
          </div>
          <h2 className="text-2xl font-extrabold">Oportunidade não encontrada</h2>
          <p className="text-gray-500 mt-2">
            Não foi possível encontrar uma oportunidade com o ID fornecido.
          </p>
          <Button onClick={() => navigate('/business-opportunities')} className="mt-6" variant="outline">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Voltar para lista de oportunidades
          </Button>
        </div>
      </div>
    );
  }

  // Render the opportunity details - prioritize Hubspot data if available
  return (
    <div className="px-4 py-6 w-full max-w-[1920px] mx-auto">
      {/* Customer Header Section */}
      <div className="bg-white rounded-lg border p-6 mb-6">
        <div className="flex items-start justify-between">
          {/* Left side: company info */}
          <div className="flex items-start gap-4">
            <Avatar className="h-16 w-16 border border-gray-200">
              {isHubspotOpportunity && hubspotOpportunity?.customer?.domain && !failedImages[hubspotOpportunity.id] ? (
                <AvatarImage 
                  src={`https://cdn.brandfetch.io/logo?domain=${hubspotOpportunity.customer.domain}`} 
                  alt={hubspotOpportunity.customer?.name || ''} 
                  onError={() => handleImageError(hubspotOpportunity.id)}
                />
              ) : isHubspotOpportunity && hubspotOpportunity?.customer?.logo_url && !failedImages[hubspotOpportunity.id] ? (
                <AvatarImage 
                  src={hubspotOpportunity.customer.logo_url} 
                  alt={hubspotOpportunity.customer?.name || ''} 
                  onError={() => handleImageError(hubspotOpportunity.id)}
                />
              ) : opportunity?.customer?.logo_url && !failedImages[opportunity.id] ? (
                <AvatarImage 
                  src={opportunity.customer.logo_url} 
                  alt={opportunity.customer?.name || ''} 
                  onError={() => handleImageError(opportunity.id)}
                />
              ) : (
                <AvatarFallback className="bg-gray-100 text-gray-600 font-extrabold text-xl">
                  {isHubspotOpportunity && hubspotOpportunity?.customer?.name 
                    ? getInitials(hubspotOpportunity.customer.name) 
                    : (opportunity?.customer?.name ? getInitials(opportunity.customer.name) : '?')}
                </AvatarFallback>
              )}
            </Avatar>
            
            <div>
              <h1 className="text-2xl font-extrabold">
                {isHubspotOpportunity && hubspotOpportunity
                  ? hubspotOpportunity.dealname
                  : opportunity?.name || 'Oportunidade'}
              </h1>
              
              <div className="flex items-center gap-3 mt-1">
                <Badge className={`${
                  isHubspotOpportunity && hubspotOpportunity
                    ? (hubspotOpportunity?.label 
                        ? statusColors[mapHubspotStageToStatus(hubspotOpportunity.label)]
                        : hubspotOpportunity?.dealstage 
                          ? statusColors[mapHubspotStageToStatus(hubspotOpportunity.dealstage)]
                          : 'bg-blue-50 text-blue-700 border-blue-200')
                    : (opportunity?.status ? statusColors[opportunity.status] : 'bg-gray-50 text-gray-700 border-gray-200')
                } border text-sm px-3 py-1`}>
                {
                  isHubspotOpportunity && hubspotOpportunity
                    ? (hubspotOpportunity?.label || (hubspotOpportunity?.dealstage ? getHubspotStageLabel(hubspotOpportunity.dealstage) : 'Não definido'))
                    : (opportunity?.status ? statusLabels[opportunity.status as keyof typeof statusLabels] : 'Desconhecido')
                }
                </Badge>
                
                <div className="flex items-center gap-1 text-sm">
                  <span className="font-extrabold">Probabilidade:</span>
                  <span>
                    {isHubspotOpportunity && hubspotOpportunity && typeof hubspotOpportunity?.hs_deal_stage_probability === 'number'
                      ? (hubspotOpportunity.hs_deal_stage_probability <= 1 
                        ? Math.round(hubspotOpportunity.hs_deal_stage_probability * 100)
                        : Math.round(hubspotOpportunity.hs_deal_stage_probability))
                      : opportunity && typeof opportunity.probability === 'number'
                        ? Math.round(opportunity.probability * 100)
                        : 0
                    }%
                  </span>
                </div>
              </div>
              
              <p className="text-sm text-muted-foreground mt-2">
                {isHubspotOpportunity && hubspotOpportunity?.customer?.name 
                  ? hubspotOpportunity.customer.name 
                  : opportunity?.customer?.name || 'Cliente'}
              </p>
              
              <div className="flex items-center gap-2 mt-1">
                {isHubspotOpportunity && hubspotOpportunity?.customer?.business_identification_number ? (
                  <div className="text-sm text-muted-foreground">
                    CNPJ: {formatDocument(hubspotOpportunity.customer.business_identification_number)}
                  </div>
                ) : opportunity?.customer?.business_identification_number ? (
                  <div className="text-sm text-muted-foreground">
                    CNPJ: {formatDocument(opportunity.customer.business_identification_number)}
                  </div>
                ) : null}
                
                {(isHubspotOpportunity && hubspotOpportunity?.customer?.business_identification_number) || 
                 (opportunity?.customer?.business_identification_number) ? (
                  <span className="text-muted-foreground">•</span>
                ) : null}
                
                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 hover:bg-green-50">
                  Ativo
                </Badge>
              </div>
              
              <div className="flex items-center mt-2">
                {isHubspotOpportunity && hubspotOpportunity?.customer?.domain ? (
                  <div className="flex items-center text-sm text-blue-600">
                    <Link className="h-3.5 w-3.5 mr-1" />
                    <a 
                      href={`https://${hubspotOpportunity.customer.domain}`} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="hover:underline"
                    >
                      {hubspotOpportunity.customer.domain}
                    </a>
                  </div>
                ) : null}
              </div>
              
              <div className="flex items-center gap-2 text-xs text-muted-foreground mt-2">
                <span>
                  Criado em: {
                    isHubspotOpportunity && hubspotOpportunity?.createdate 
                      ? format(new Date(hubspotOpportunity.createdate), 'dd/MM/yyyy')
                      : (opportunity?.created_at ? format(new Date(opportunity.created_at), 'dd/MM/yyyy') : 'N/A')
                  }
                </span>
                
                <span className="mx-1">•</span>
                
                <span>
                  Atualizado em: {
                    isHubspotOpportunity && hubspotOpportunity?.hs_lastmodifieddate 
                      ? format(new Date(hubspotOpportunity.hs_lastmodifieddate), 'dd/MM/yyyy')
                      : (opportunity?.updated_at ? format(new Date(opportunity.updated_at), 'dd/MM/yyyy') : 'N/A')
                  }
                </span>

                {(isHubspotOpportunity && hubspotOpportunity?.owner_name) && (
                  <>
                    <span className="mx-1">•</span>
                    <span>
                      Vendedor: {hubspotOpportunity.owner_name}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
          
          {/* Right side: action buttons */}
          <div className="flex items-center gap-3">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleBackClick}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Voltar
            </Button>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <MoreHorizontal className="h-4 w-4 mr-2" />
                  Mais Ações
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => window.open(
                  isHubspotOpportunity && hubspotOpportunity.hs_object_id 
                    ? `https://app.hubspot.com/contacts/9301423/deal/${hubspotOpportunity.hs_object_id}`
                    : '', '_blank')}>
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Ver no HubSpot
                </DropdownMenuItem>
                {isHubspotOpportunity && hubspotOpportunity?.customer?.id && (
                  <DropdownMenuItem onClick={() => navigate(`/customers/${hubspotOpportunity.customer.id}`)}>
                    <Users className="h-4 w-4 mr-2" />
                    Ver Detalhes do Cliente
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
      
      {/* Financial Metrics */}
      {isHubspotOpportunity && hubspotOpportunity && (
        <div className="mb-8">
          <h2 className="text-xl font-extrabold mb-4">Métricas Financeiras</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* One-Time */}
            <div className="bg-white rounded-lg border p-4">
              <div className="flex justify-between items-center mb-2">
                <div className="font-extrabold">One-Time</div>
                <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-xs">Único</span>
              </div>
              <div className="text-2xl font-extrabold mb-1">
                {formatCurrency(hubspotOpportunity.hs_onetime || 0, hubspotOpportunity.deal_currency_code || 'BRL')}
              </div>
              <div className="flex items-center justify-between">
                <div className="text-xs text-muted-foreground">
                  One-Time Payments
                </div>
                {(hubspotOpportunity.hs_margin_onetime !== undefined && hubspotOpportunity.hs_margin_onetime !== null) && (
                  <div className="flex items-center gap-1">
                    <span className={`text-xs font-extrabold ${Number(hubspotOpportunity.hs_margin_onetime) >= 40 ? 'text-green-600' : Number(hubspotOpportunity.hs_margin_onetime) >= 20 ? 'text-amber-600' : 'text-red-600'}`}>
                      {hubspotOpportunity.hs_margin_onetime}%
                    </span>
                    <span className="text-xs text-muted-foreground">margem</span>
                  </div>
                )}
              </div>
            </div>

            {/* MRR */}
            <div className="bg-white rounded-lg border p-4">
              <div className="flex justify-between items-center mb-2">
                <div className="font-extrabold">MRR</div>
                <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs">Mensal</span>
              </div>
              <div className="text-2xl font-extrabold mb-1">
                {formatCurrency(hubspotOpportunity.hs_mrr || 0, hubspotOpportunity.deal_currency_code || 'BRL')}
              </div>
              <div className="flex items-center justify-between">
                <div className="text-xs text-muted-foreground">
                  Monthly Recurring Revenue
                </div>
                {(hubspotOpportunity.hs_margin_mrr !== undefined && hubspotOpportunity.hs_margin_mrr !== null) && (
                  <div className="flex items-center gap-1">
                    <span className={`text-xs font-extrabold ${Number(hubspotOpportunity.hs_margin_mrr) >= 40 ? 'text-green-600' : Number(hubspotOpportunity.hs_margin_mrr) >= 20 ? 'text-amber-600' : 'text-red-600'}`}>
                      {hubspotOpportunity.hs_margin_mrr}%
                    </span>
                    <span className="text-xs text-muted-foreground">margem</span>
                  </div>
                )}
              </div>
            </div>

            {/* ARR */}
            <div className="bg-white rounded-lg border p-4">
              <div className="flex justify-between items-center mb-2">
                <div className="font-extrabold">ARR</div>
                <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs">Anual</span>
              </div>
              <div className="text-2xl font-extrabold mb-1">
                {formatCurrency(hubspotOpportunity.hs_arr || 0, hubspotOpportunity.deal_currency_code || 'BRL')}
              </div>
              <div className="flex items-center justify-between">
                <div className="text-xs text-muted-foreground">
                  Annual Recurring Revenue
                </div>
                {(hubspotOpportunity.hs_margin_arr !== undefined && hubspotOpportunity.hs_margin_arr !== null) && (
                  <div className="flex items-center gap-1">
                    <span className={`text-xs font-extrabold ${Number(hubspotOpportunity.hs_margin_arr) >= 40 ? 'text-green-600' : Number(hubspotOpportunity.hs_margin_arr) >= 20 ? 'text-amber-600' : 'text-red-600'}`}>
                      {hubspotOpportunity.hs_margin_arr}%
                    </span>
                    <span className="text-xs text-muted-foreground">margem</span>
                  </div>
                )}
              </div>
            </div>

            {/* ACV */}
            <div className="bg-white rounded-lg border p-4">
              <div className="flex justify-between items-center mb-2">
                <div className="font-extrabold">ACV</div>
                <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs">Primeiro Ano</span>
              </div>
              <div className="text-2xl font-extrabold mb-1">
                {formatCurrency(hubspotOpportunity.hs_acv || 0, hubspotOpportunity.deal_currency_code || 'BRL')}
              </div>
              <div className="flex items-center justify-between">
                <div className="text-xs text-muted-foreground">
                  Annual Contract Value
                </div>
                {(hubspotOpportunity.hs_margin_acv !== undefined && hubspotOpportunity.hs_margin_acv !== null) && (
                  <div className="flex items-center gap-1">
                    <span className={`text-xs font-extrabold ${Number(hubspotOpportunity.hs_margin_acv) >= 40 ? 'text-green-600' : Number(hubspotOpportunity.hs_margin_acv) >= 20 ? 'text-amber-600' : 'text-red-600'}`}>
                      {hubspotOpportunity.hs_margin_acv}%
                    </span>
                    <span className="text-xs text-muted-foreground">margem</span>
                  </div>
                )}
              </div>
            </div>

            {/* TCV */}
            <div className="bg-white rounded-lg border p-4">
              <div className="flex justify-between items-center mb-2">
                <div className="font-extrabold">TCV</div>
                <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs">Duração Total</span>
              </div>
              <div className="text-2xl font-extrabold mb-1">
                {formatCurrency(hubspotOpportunity.hs_tcv || 0, hubspotOpportunity.deal_currency_code || 'BRL')}
              </div>
              <div className="flex items-center justify-between">
                <div className="text-xs text-muted-foreground">
                  Total Contract Value
                </div>
                {(hubspotOpportunity.hs_margin_tcv !== undefined && hubspotOpportunity.hs_margin_tcv !== null) && (
                  <div className="flex items-center gap-1">
                    <span className={`text-xs font-extrabold ${Number(hubspotOpportunity.hs_margin_tcv) >= 40 ? 'text-green-600' : Number(hubspotOpportunity.hs_margin_tcv) >= 20 ? 'text-amber-600' : 'text-red-600'}`}>
                      {hubspotOpportunity.hs_margin_tcv}%
                    </span>
                    <span className="text-xs text-muted-foreground">margem</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Line Items Section */}
      {isHubspotOpportunity && hubspotOpportunity?.line_items && (
        <Card className="mb-8">
          <CardHeader className="pb-3">
            <div className="flex justify-between items-center">
              <CardTitle className="text-xl flex items-center">
                <Package className="mr-2 h-5 w-5 text-muted-foreground" />
                Itens da Oportunidade
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            {hubspotOpportunity.line_items.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Quantidade</TableHead>
                    <TableHead>Preço Unitário</TableHead>
                    <TableHead>Desconto</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {hubspotOpportunity.line_items.map((item: HubspotLineItem) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-extrabold">
                        <div>
                          {item.name}
                          {item.description && (
                            <p className="text-xs text-muted-foreground mt-1">{item.description}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{item.quantity}</TableCell>
                      <TableCell>
                        {formatCurrency(item.price, hubspotOpportunity.deal_currency_code || 'BRL')}
                      </TableCell>
                      <TableCell>
                        {item.discount > 0 ? `${item.discount}%` : '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(item.amount, hubspotOpportunity.deal_currency_code || 'BRL')}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-8">
                <Package className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                <p className="text-muted-foreground">
                  Nenhum item encontrado para esta oportunidade
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}
      
      {/* Quotes Section */}
      {isHubspotOpportunity && hubspotOpportunity && (
        <Card className="mb-8">
          <CardHeader className="pb-3">
            <div className="flex justify-between items-center">
              <CardTitle className="text-xl flex items-center">
                <FileText className="mr-2 h-5 w-5 text-muted-foreground" />
                Cotações
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            {hubspotOpportunity.quotes && hubspotOpportunity.quotes.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Número</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Valor</TableHead>
                    <TableHead>Data de Criação</TableHead>
                    <TableHead>Válido até</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {hubspotOpportunity.quotes.map((quote: any) => (
                    <TableRow 
                      key={quote.id} 
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => handleQuoteClick(quote)}
                    >
                      <TableCell className="font-extrabold">{quote.hs_quote_number || quote.id}</TableCell>
                      <TableCell>
                        <Badge variant={
                          quote.hs_status === 'APPROVED' ? 'success' :
                          quote.hs_status === 'PENDING' ? 'outline' :
                          quote.hs_status === 'REJECTED' ? 'destructive' : 'secondary'
                        }>
                          {quote.hs_status === 'APPROVED' ? 'Aprovado' :
                           quote.hs_status === 'PENDING' ? 'Pendente' :
                           quote.hs_status === 'REJECTED' ? 'Rejeitado' : quote.hs_status || 'Desconhecido'}
                        </Badge>
                      </TableCell>
                      <TableCell>{quote.hs_quote_amount ? formatCurrency(quote.hs_quote_amount, quote.hs_currency || 'BRL') : 'N/A'}</TableCell>
                      <TableCell>{quote.hs_createdate ? new Date(quote.hs_createdate).toLocaleDateString() : 'N/A'}</TableCell>
                      <TableCell>{quote.hs_expiration_date ? new Date(quote.hs_expiration_date).toLocaleDateString() : 'N/A'}</TableCell>
                      <TableCell className="text-right">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/proposals/${quote.id}`);
                          }}
                        >
                          Ver Detalhes
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-8">
                <FileText className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                <p className="text-muted-foreground">
                  Nenhuma cotação encontrada para esta oportunidade
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default BusinessOpportunityDetail; 